import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Maximize2, MonitorSmartphone, UserCheck, Repeat, Shapes } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import PaintHero from "@/components/PaintHero";
import BrushDivider from "@/components/BrushDivider";
import Marquee from "@/components/Marquee";
import BeforeAfter from "@/components/BeforeAfter";
import Reveal, { Eyebrow } from "@/components/Reveal";

const PROCESS = [
  { n: "01", t: "TELL US", d: "Send a brief through the project request form. References, files, deadlines — whatever you have." },
  { n: "02", t: "WE CREATE", d: "Your project is queued, designed and reviewed by a human — not auto-generated and shipped." },
  { n: "03", t: "REVIEW & REFINE", d: "You review the work in your portal and request revisions until it's right." },
  { n: "04", t: "DELIVERED", d: "Final files land in your portal, ready to download — source files included where agreed." },
];

const WHY = [
  { icon: Maximize2, t: "ANY SIZE PROJECT", d: "From a single icon to a full brand identity." },
  { icon: MonitorSmartphone, t: "DIGITAL FIRST", d: "We understand modern communities, creators, games and online businesses." },
  { icon: UserCheck, t: "HUMAN REVIEW", d: "Every project is reviewed and refined — never auto-generated and delivered." },
  { icon: Repeat, t: "FLEXIBLE", d: "One-off projects or ongoing subscriptions. Scale up or down as you need." },
  { icon: Shapes, t: "BUILT AROUND YOU", d: "We adapt to your brand — client work never gets forced into the Arsenal style." },
];

const RED_TEX = "https://images.unsplash.com/photo-1671494685095-68b5c92963e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwcmVkJTIwY3JpbXNvbiUyMHBhaW50JTIwdGV4dHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fDE3OTA3Mjk5NDZ8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [services, setServices] = useState([]);
  const [work, setWork] = useState([]);
  const [beforeAfter, setBeforeAfter] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/portfolio").then((r) => {
      setWork(r.data.slice(0, 6));
      setBeforeAfter(r.data.find((p) => p.before_after) || null);
    }).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title={null}
        description="Arsenal Artists is a digital creative studio for logos, branding, graphics, image editing and video editing. One-off projects or monthly creative subscriptions."
        path="/"
      />
      <PaintHero />
      <BrushDivider />

      {/* 02 — WHAT WE DO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="home-services">
        <Reveal>
          <Eyebrow>02 / WHAT WE DO</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-xl text-balance">
              Six disciplines. One studio. <span className="text-arsenal">Zero templates.</span>
            </h2>
            <Link to="/services" data-testid="home-services-all" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-slate-400 hover:text-arsenal transition-colors">
              ALL SERVICES <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <Link
                to={`/services#${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className="group relative block h-full bg-ink-surface hover:bg-ink-elevated p-7 transition-colors duration-300"
              >
                <span className="font-mono text-[11px] text-slate-600">0{i + 1}</span>
                <h3 className="mt-3 font-display text-xl font-bold text-white group-hover:text-arsenal transition-colors">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.tagline}</p>
                <ArrowUpRight className="absolute top-6 right-6 h-5 w-5 text-slate-600 group-hover:text-arsenal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-arsenal transition-all duration-500 group-hover:w-full" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* 03 — SELECTED WORK */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="home-selected-work">
        <Reveal>
          <Eyebrow>03 / SELECTED WORK</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-xl text-balance">
              The work speaks. <span className="text-arsenal">We just make it louder.</span>
            </h2>
            <Link to="/portfolio" data-testid="home-portfolio-all" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-slate-400 hover:text-arsenal transition-colors">
              FULL PORTFOLIO <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          {work.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.08}>
              <Link to={`/portfolio/${p.slug}`} data-testid={`home-work-${p.slug}`} className="group relative block overflow-hidden border border-white/10">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {p.categories.map((c) => (
                      <span key={c} className="clip-tag bg-arsenal/90 px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-white">{c.toUpperCase()}</span>
                    ))}
                    {p.sample && <span className="clip-tag bg-white/10 px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-slate-300">CONCEPT</span>}
                  </div>
                  <h3 className="font-display text-lg sm:text-xl font-bold text-white">{p.title}</h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 04 — PROCESS */}
      <section className="border-y border-white/5 bg-ink-surface/50" data-testid="home-process">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <Eyebrow>04 / FROM AN IDEA TO FINISHED WORK</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-2xl text-balance">
              Tell us what you need. <span className="text-arsenal">We'll handle the rest.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="relative">
                  <span className="font-display text-5xl font-black text-transparent" style={{ WebkitTextStroke: "1px rgba(229,9,20,0.5)" }}>{p.n}</span>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-wide text-white">{p.t}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.d}</p>
                  {i < 3 && <span className="hidden lg:block absolute top-7 -right-5 h-px w-6 bg-arsenal/50" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 05 — SUBSCRIPTIONS + 06 — ONE-OFF */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-6" data-testid="home-models">
        <Reveal>
          <div className="relative h-full overflow-hidden border border-arsenal/30 bg-gradient-to-b from-arsenal/10 to-transparent p-8 sm:p-10">
            <Eyebrow>05 / CREATIVE SUBSCRIPTIONS</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-balance">Need a creative team every month? We can do that.</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Subscribe and submit requests as you need them — graphics, thumbnails, edits, branding support. Priority queues on higher tiers.
            </p>
            <Link to="/subscriptions" data-testid="home-subscriptions-cta" className="clip-btn mt-8 inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-sm px-7 py-3.5 transition-colors">
              SEE PLANS <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative h-full overflow-hidden border border-white/10 bg-ink-surface p-8 sm:p-10">
            <Eyebrow>06 / ONE-OFF PROJECTS</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-balance">Need just one thing? No problem.</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              A logo, a thumbnail, a banner, a single edit — tell us what you need and we'll quote it. No commitment required.
            </p>
            <Link to="/pricing" data-testid="home-pricing-cta" className="clip-btn mt-8 inline-flex items-center gap-2 border border-white/20 hover:border-arsenal hover:text-arsenal text-white font-display font-bold tracking-widest text-sm px-7 py-3.5 transition-colors">
              GET A QUOTE <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* 07 — WHY */}
      <section className="border-y border-white/5 bg-ink-surface/50" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <Eyebrow>07 / WHY ARSENAL ARTISTS</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-2xl text-balance">
              Your brand leads. <span className="text-arsenal">Ours stays home.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.06} className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div className="h-full bg-ink-surface p-7 group hover:bg-ink-elevated transition-colors duration-300">
                  <w.icon className="h-6 w-6 text-arsenal" />
                  <h3 className="mt-4 font-display text-base font-bold tracking-wider text-white">{w.t}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 08 — BEFORE / AFTER (only shown once a real before/after project exists) */}
      {beforeAfter && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="home-before-after">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <Eyebrow>08 / BEFORE & AFTER</Eyebrow>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-balance">
                Drag the line. <span className="text-arsenal">See the difference.</span>
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
                Retouching, restoration, rebrands and manipulation — the gap between raw and finished is where we work.
              </p>
              <Link to={`/portfolio/${beforeAfter.slug}`} data-testid="home-before-after-link" className="group mt-6 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-slate-400 hover:text-arsenal transition-colors">
                VIEW PROJECT <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <BeforeAfter before={beforeAfter.before_after.before} after={beforeAfter.before_after.after} alt={beforeAfter.title} />
            </Reveal>
          </div>
        </section>
      )}

      {/* 09 — TESTIMONIALS (only when real ones exist) */}
      {testimonials.length > 0 && (
        <section className="border-y border-white/5 bg-ink-surface/50" data-testid="home-testimonials">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
            <Reveal>
              <Eyebrow>09 / TESTIMONIALS</Eyebrow>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Word from clients.</h2>
            </Reveal>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <blockquote key={t.id} className="border border-white/10 bg-ink-surface p-7" data-testid={`testimonial-${t.id}`}>
                  <p className="text-slate-300 leading-relaxed">"{t.testimonial}"</p>
                  <footer className="mt-5 flex items-center gap-3">
                    {t.avatar && <img src={t.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />}
                    <div>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.company}</p>
                    </div>
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 10 — FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="home-final-cta">
        <div className="absolute inset-0 opacity-20" aria-hidden>
          <img src={RED_TEX} alt="" className="h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/80 to-ink" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36 text-center">
          <Reveal>
            <img src="/arsenal-artists-logo.png" alt="" className="mx-auto h-20 w-20 object-contain mb-8 opacity-90" loading="lazy" />
            <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight text-balance">
              LET'S CREATE <span className="text-arsenal">SOMETHING.</span>
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/start-a-project" data-testid="final-cta-start" className="clip-btn inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-sm px-9 py-4 transition-colors shadow-[0_0_40px_rgba(229,9,20,0.4)]">
                START A PROJECT <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/portfolio" data-testid="final-cta-work" className="clip-btn inline-flex items-center gap-2 border border-white/20 hover:border-arsenal hover:text-arsenal text-white font-display font-bold tracking-widest text-sm px-9 py-4 transition-colors">
                VIEW OUR WORK
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
